import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { fetchDashboardData } from "../../Redux/Thunks/dashboardThunk";
import {
  selectDashboardData,
  selectDashboardLoading,
  selectDashboardError,
} from "../../Redux/Slices/dashboardSlice";
import { ContentLoader, Loader } from "../../Utility/Loader";
import NoDataFound from "../../Utility/NoDataFound/NoDataFound";
import Typography from "../../Utility/Typography";
import DashboardStatistics from "./DashboardStatistics";
import BugStatistics from "./BugStatistics";
import TeamWorkload from "./TeamWorkload";
import DateRangePicker from "../../Utility/DatePickerRange/DatePickerRange";
import Dropdown from "../../Utility/Dropdown/Dropdown";
import Avatar from "../../Utility/Avatar/Avatar";
import {
  formatActivityTimeOrString,
  getDashboardSummaryCounts,
  mapRecentActivityItems,
  mapRecentProjectChangeItems,
  shouldShowOverloadedTeams,
  shouldShowRecentProjectChanges,
} from "../../utils/dashboardPayload";
import { getDashboardStatsCards } from "../../utils/dashboardStatsCards";
import { buildDashboardQueryParams } from "../../utils/dashboardQueryParams";
import { ROUTES } from "../../constants";

const Dashboard = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const dashboardData = useSelector(selectDashboardData);
  const loading = useSelector(selectDashboardLoading);
  const error = useSelector(selectDashboardError);
  const [selectedTeam, setSelectedTeam] = useState("All");
  /** `{ startDate, endDate }` strings YYYY-MM-DD — matches DateRangePicker */
  const [taskDateRange, setTaskDateRange] = useState(null);
  const [bugDateRange, setBugDateRange] = useState(null);
  /** Before the first fetch settles, `loading` can still be false on the first paint — use this to show a loader until pending/fulfilled/rejected. */
  const hasFetchSettledOnce = useRef(false);
  useEffect(() => {
    if (!loading) {
      hasFetchSettledOnce.current = true;
    }
  }, [loading]);

  useEffect(() => {
    const params = buildDashboardQueryParams(taskDateRange, bugDateRange);
    dispatch(fetchDashboardData(params));
  }, [dispatch, taskDateRange, bugDateRange]);

  // If Redux already has dashboard data (e.g. navigated back), do not block on first-paint ref.
  const showFullPageLoader = loading || (!dashboardData && !hasFetchSettledOnce.current);

  if (error && !loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <Typography variant="text1" className="text-red-600 text-lg">
          {error}
        </Typography>
      </div>
    );
  }

  if (showFullPageLoader) {
    return <Loader variant="fullScreen" />;
  }

  if (!dashboardData) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <NoDataFound message="No dashboard data available" />
      </div>
    );
  }

  const counts = getDashboardSummaryCounts(dashboardData);
  const statsCards = getDashboardStatsCards(counts);
  const welcomeName = dashboardData.userName?.trim() || "there";
  const activities = mapRecentActivityItems(dashboardData.recentActivity);
  const showOverloadedTeams = shouldShowOverloadedTeams(dashboardData);
  const showRecentProjectChanges = shouldShowRecentProjectChanges(dashboardData);
  const overloaded = showOverloadedTeams ? dashboardData.overloadedTeams : null;
  const teams = overloaded?.teams ?? [];
  const teamMembersData = overloaded?.teamMembers ?? {};
  const projectChanges = mapRecentProjectChangeItems(dashboardData.recentProjectChanges);
  const activeBlockers = dashboardData.activeBlockers ?? [];
  const blockersOverdueCount = dashboardData.blockersOverdueCount ?? 0;

  const teamOptions = [
    "All",
    ...teams.map((team) => String(team?.teamName ?? team?.name ?? "").trim()).filter(Boolean),
  ];

  const handleTeamChange = (e) => {
    const next = e?.target?.value;
    if (next != null) setSelectedTeam(next);
  };

  const navigateFromSummaryCard = (cardLink) => {
    if (!cardLink) return;
    if (cardLink.type === "path" && cardLink.to) {
      navigate(cardLink.to);
      return;
    }
    if (cardLink.type === "workItems" && cardLink.workItemType) {
      const q = new URLSearchParams({ tab: "all-issue", workItemType: cardLink.workItemType });
      navigate(`${ROUTES.ISSUES}?${q.toString()}`);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <ContentLoader loading={loading} className="min-h-[calc(100vh-12rem)]">
        <div className="mx-auto max-w-[1600px] px-2 sm:px-4">
          {/* 1. Welcome — displays `userName` from dashboard API */}
          <div className="mb-5 sm:mb-5">
            <Typography variant="h1" className="text-center">
              <Typography variant="h1" className="text-gray-900" component="span">
                Welcome
              </Typography>{" "}
              <Typography variant="h1" className="text-brand-primary" component="span">
                {welcomeName}!
              </Typography>
            </Typography>
          </div>

          {/* 2. Summary cards — Clients, Projects, Tasks, Bugs (`summary` counts from API) */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 mb-6 sm:mb-8">
            {statsCards.map((card) => {
              const cardClass = `shadow-md py-2 px-4 h-[120px] ${card.bg} rounded flex space-between items-center`;
              const inner = (
                <div className="w-full">
                  <Typography variant="h2" className="text-white font-bold text-xl sm:text-2xl">
                    {card.title}
                  </Typography>
                  <div className="flex rounded items-center justify-between">
                    <Typography variant="text2" className="text-white mt-2 opacity-90 md:text-2xl">
                      {card.value.toLocaleString()}
                    </Typography>
                    <div className="w-[40px] h-[40px] bg-white rounded flex items-center justify-center">
                      <svg
                        className="w-6 h-6 sm:w-8 sm:h-8 text-dark"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d={card.iconPath}
                        />
                      </svg>
                    </div>
                  </div>
                </div>
              );
              const link = card.cardLink;
              const ariaGo =
                link?.type === "path"
                  ? `Open ${card.title}`
                  : link?.type === "workItems"
                    ? `Open all work items filtered by ${link.workItemType}`
                    : `Open ${card.title}`;
              return (
                <button
                  key={card.title}
                  type="button"
                  onClick={() => navigateFromSummaryCard(link)}
                  className={`${cardClass} w-full text-left cursor-pointer transition hover:brightness-110 focus:outline-none focus-visible:ring-2 focus-visible:ring-white/70 focus-visible:ring-offset-2 focus-visible:ring-offset-gray-50`}
                  aria-label={ariaGo}
                >
                  {inner}
                </button>
              );
            })}
          </div>

          {/* 3–4. Charts row — task vs bug work-item stats (`taskStatistics` / `bugStatistics`) */}
          <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-2 gap-4 sm:gap-6 mb-6 sm:mb-8">
            {/* 3. Task Statistics chart + optional date range filter */}
            <div
              className="flex flex-col bg-white border border-gray-200 shadow-lg card-border"
              style={{ overflow: "visible" }}
            >
              <div
                className="px-4 py-2 border-b border-gray-200 rounded-tl-[50px] rounded-tr-[20px] flex items-center justify-between min-h-[48px]"
                style={{ overflow: "visible" }}
              >
                <Typography variant="h4">Task Statistics</Typography>

                <div className="w-64">
                  <DateRangePicker
                    value={taskDateRange ?? undefined}
                    onChange={(range) => setTaskDateRange(range)}
                    showIcon
                  />
                </div>
              </div>
              <div className="p-4 md:p-5">
                <DashboardStatistics stats={dashboardData.taskStatistics ?? null} />
              </div>
            </div>

            {/* 4. Bug Statistics chart + optional date range filter */}
            <div
              className="lg:col-span-1 flex flex-col bg-white border border-gray-200 shadow-lg card-border"
              style={{ overflow: "visible" }}
            >
              <div
                className="px-4 py-2 border-b border-gray-200 rounded-tl-[50px] rounded-tr-[20px] flex items-center justify-between min-h-[48px]"
                style={{ overflow: "visible" }}
              >
                <Typography variant="h4">Bug Statistics</Typography>
                <div className="w-64">
                  <DateRangePicker
                    value={bugDateRange ?? undefined}
                    onChange={(range) => setBugDateRange(range)}
                    showIcon
                  />
                </div>
              </div>
              <div className="p-4 md:p-5">
                <BugStatistics stats={dashboardData.bugStatistics ?? null} />
              </div>
            </div>
          </div>

          {/* 5–6. Activity + workload — recent audit log and team workload chart */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-5">
            {/* 5. Recent Activity — `recentActivity[]` (title, name, time) */}
            <div
              className={`flex flex-col bg-white border border-gray-200 shadow-lg card-border max-h-[400px] overflow-y-auto ${
                showOverloadedTeams ? "lg:col-span-1" : "lg:col-span-2"
              }`}
            >
              <div className="px-4 py-2 border-b border-gray-200 rounded-tl-[50px] rounded-tr-[20px] flex items-center min-h-[48px]">
                <Typography variant="h4">Recent Activity</Typography>
              </div>
              <div className="space-y-4 py-5 px-4 max-h-[4000px] overflow-y-auto">
                {activities.length === 0 ? (
                  <Typography variant="text2" className="text-gray-500 text-center py-4">
                    No recent activity
                  </Typography>
                ) : null}
                {activities.map((item, index) => (
                  <div
                    key={item.actorId ?? index}
                    className="flex items-start gap-3 border-b border-gray-100 pb-3 last:border-0"
                  >
                    <Avatar
                      name={item.initials}
                      className="!w-9 !h-9 !min-w-[36px] !min-h-[36px] !text-xs shrink-0"
                    />
                    <div className="flex min-w-0 flex-1 flex-col gap-0.5 sm:flex-row sm:items-start sm:justify-between sm:gap-3">
                      <div className="min-w-0 flex-1">
                        <Typography
                          variant="span2"
                          className="text-sm font-medium text-gray-800"
                          component="h6"
                        >
                          {item.headline}
                        </Typography>
                        {item.actorName ? (
                          <Typography variant="span2" className="text-xs text-gray-500">
                            {item.actorName}
                          </Typography>
                        ) : null}
                      </div>
                      <Typography
                        variant="span2"
                        className="text-xs text-gray-400 whitespace-nowrap shrink-0 sm:pt-0.5"
                      >
                        {item.time?.trim() ? item.time : "—"}
                      </Typography>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* 6. Overloaded Teams — `overloadedTeams.teams` + `teamMembers` workload chart */}
            {showOverloadedTeams ? (
              <div className="lg:col-span-1 flex flex-col bg-white border border-gray-200 shadow-lg card-border">
                <div className="px-4 py-2 border-b border-gray-200 rounded-tl-[50px] rounded-tr-[20px] flex items-center justify-between min-h-[48px]">
                  <Typography variant="h4">Overloaded Teams</Typography>
                  <div className="w-40">
                    <Dropdown
                      name="team"
                      value={selectedTeam}
                      onChange={handleTeamChange}
                      options={teamOptions}
                      placeholder="Select Team"
                      noWrapper={true}
                      className="text-sm"
                    />
                  </div>
                </div>
                <div className="p-4 md:p-5">
                  <TeamWorkload
                    selectedTeam={selectedTeam}
                    teamsData={teams}
                    teamMembersData={teamMembersData}
                  />
                </div>
              </div>
            ) : null}
          </div>

          {/* 7–8. Project feed + blockers — audit trail and overdue blocker list */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-5 ">
            {/* 7. Recent Project Changes — same row pattern as Recent Activity (actor, time) */}
            {showRecentProjectChanges ? (
              <div className="lg:col-span-1 flex flex-col bg-white border border-gray-200 shadow-lg card-border ">
                <div className="px-4 py-2 border-b border-gray-200 rounded-tl-[50px] rounded-tr-[20px] flex items-center min-h-[48px]">
                  <Typography variant="h4">Recent Project Changes</Typography>
                </div>
                <div
                  className="space-y-4 py-5 px-4
                max-h-[350px] overflow-y-auto"
                >
                  {projectChanges.map((item) => (
                    <div
                      key={item.id}
                      className="flex items-start gap-3 border-b border-gray-100 pb-3 last:border-0"
                    >
                      <Avatar
                        name={item.initials}
                        className="!w-9 !h-9 !min-w-[36px] !min-h-[36px] !text-xs shrink-0"
                      />
                      <div className="flex min-w-0 flex-1 flex-col gap-0.5 sm:flex-row sm:items-start sm:justify-between sm:gap-3">
                        <div className="min-w-0 flex-1">
                          {item.isLegacy && item.icon ? (
                            <Typography
                              variant="span2"
                              className="text-sm font-medium text-gray-800 inline-flex items-center gap-2"
                              component="h6"
                            >
                              <i
                                className={`${item.icon} ${item.color ?? "text-gray-500"} text-base shrink-0`}
                                aria-hidden
                              />
                              {item.headline}
                            </Typography>
                          ) : (
                            <Typography
                              variant="span2"
                              className="text-sm font-medium text-gray-800"
                              component="h6"
                            >
                              {item.headline}
                            </Typography>
                          )}
                          {item.actorName ? (
                            <Typography variant="span2" className="text-xs text-gray-500">
                              {item.actorName}
                            </Typography>
                          ) : null}
                        </div>
                        <Typography
                          variant="span2"
                          className="text-xs text-gray-400 whitespace-nowrap shrink-0 sm:pt-0.5"
                        >
                          {item.time?.trim() ? item.time : "—"}
                        </Typography>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : null}

            {/* 8. Active Blockers — `activeBlockers[]` + `blockersOverdueCount` */}
            <div
              className={`flex flex-col bg-white border border-gray-200 shadow-lg card-border max-h-[400px] overflow-y-auto ${
                showRecentProjectChanges ? "lg:col-span-1" : "lg:col-span-2"
              }`}
            >
              <div className="px-4 py-2 border-b border-gray-200 rounded-tl-[50px] rounded-tr-[20px] flex items-center justify-between min-h-[48px]">
                <Typography variant="h4">Active Blockers</Typography>
                <div className="flex items-center gap-1 text-xs text-red-500 font-medium">
                  <Typography
                    variant="span2"
                    className="w-2 h-2 rounded-full bg-red-500"
                    component="span"
                  ></Typography>
                  Overdue: {blockersOverdueCount}
                </div>
              </div>
              <div className="space-y-3 p-4">
                {activeBlockers.map((item, index) => (
                  <div key={index} className="flex items-start gap-3 border-b last:border-none">
                    <div className="w-7 h-7 rounded-full bg-gray-100 flex items-center justify-center text-xs font-semibold text-gray-600">
                      {item.initial}
                    </div>
                    <div className="flex-1">
                      <Typography
                        variant="span2"
                        className="text-sm text-gray-800 font-medium"
                        component="p"
                      >
                        {item.title}
                      </Typography>
                      <Typography variant="span2" className="text-xs text-gray-500" component="p">
                        {item.company}
                      </Typography>
                    </div>
                    <span className="text-xs text-gray-400 whitespace-nowrap">
                      {formatActivityTimeOrString(item.time)}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </ContentLoader>
    </div>
  );
};

export default Dashboard;
